import { updateCompany } from "@/api/company/company-services";
import { uploadFile } from "@/api/client/communications";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { zodResolver } from "@hookform/resolvers/zod";

import Loader from "@/components/utils/loader";
import { useStore } from "@/store";
import { useState } from "react";
import { SketchPicker } from "react-color";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  alias: z.string().min(4, { message: "Alias is required" }),
  name: z.string().min(4, { message: "Nombre is required" }),
  primaryColor: z.string(),
  secondaryColor: z.string(),
  logo: z.string(),
  template: z.string(),
  hasConsultation: z.boolean(),
  notificationMode: z.enum([
    "NOTIFICATION_OFF",
    "NOTIFICATION_ON",
    "NOTIFICATION_DEBUG",
  ]),
});

const DEFAULT_COLOR = "#000000";

const CreateCompanyForm = () => {
  const { t } = useTranslation();
  const companyData = useStore((state) => state.companyData);
  const [primaryColor, setPrimaryColor] = useState(
    companyData?.primaryColor ?? DEFAULT_COLOR,
  );
  const [secondaryColor, setSecondaryColor] = useState(
    companyData?.secondaryColor ?? DEFAULT_COLOR,
  );
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      alias: companyData?.alias || "",
      name: companyData?.name || "",
      primaryColor: companyData?.primaryColor ?? DEFAULT_COLOR,
      secondaryColor: companyData?.secondaryColor ?? DEFAULT_COLOR,
      logo: companyData?.logo || "",
      template: companyData?.template || "",
      hasConsultation: companyData?.hasConsultation || false,
      notificationMode:
        companyData?.notificationMode || "NOTIFICATION_OFF",
    },
  });

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      values.primaryColor = primaryColor;
      values.secondaryColor = secondaryColor;
      
      // Si hay un archivo seleccionado, subirlo primero
      if (selectedFile) {
        console.log("Subiendo logo...");
        const uploadResponse = await uploadFile(
          selectedFile,
          undefined, // communicationId
          true, // isPublic - para que sea visible por todos
          "logo", // type
          "Company Logo", // title
          "", // questionId
          "company" // creating
        );
        
        // Asignar la URL del logo subido
        if (uploadResponse && uploadResponse.url) {
          values.logo = uploadResponse.url;
          console.log("Logo subido exitosamente:", uploadResponse.url);
        }
      }
      
      await updateCompany(values);
      console.log("Empresa actualizada exitosamente");
    } catch (error) {
      console.error("Error in company updated", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePrimaryColor = (color, event, field) => {
    setPrimaryColor(color.hex);
    field.value = color.hex;
  };
  const handleChangeSecondaryColor = (color, event, field) => {
    setSecondaryColor(color.hex);
    field.value = color.hex;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar que sea una imagen
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        console.log("Archivo seleccionado:", file.name);
      } else {
        alert(t("errors.invalidFileType") || "Por favor selecciona un archivo de imagen válido");
        event.target.value = '';
      }
    }
  };

  return (
    <section className="w-full flex justify-center">
      <Card className="p-6 w-full bg-slate-50">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.company.companyName")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("forms.company.companyName")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="alias"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.company.alias")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("forms.company.alias")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="primaryColor"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.company.primaryColor")}</FormLabel>
                  <FormControl>
                    <div className="w-full max-w-full">
                      <Controller
                        name="primaryColor"
                        control={form.control}
                        render={({ field }) => (
                          <SketchPicker
                            color={primaryColor}
                            onChange={(color, event) =>
                              handleChangePrimaryColor(color, event, field)
                            }
                            disableAlpha={true}
                            width="40%"
                          />
                        )}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="secondaryColor"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>{t("forms.company.secondaryColor")}</FormLabel>
                  <FormControl>
                    <div className="w-full max-w-full">
                      <Controller
                        name="secondaryColor"
                        control={form.control}
                        render={({ field }) => (
                          <SketchPicker
                            color={secondaryColor}
                            onChange={(color, event) =>
                              handleChangeSecondaryColor(color, event, field)
                            }
                            disableAlpha={true}
                            width="40%"
                          />
                        )}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasConsultation"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 mb-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      {t("forms.company.hasConsultation")}
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notificationMode"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Notificaciones</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar modo de notificaciones" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NOTIFICATION_OFF">
                          Desactivadas
                        </SelectItem>
                        <SelectItem value="NOTIFICATION_ON">
                          Activadas
                        </SelectItem>
                        <SelectItem value="NOTIFICATION_DEBUG">
                          Debug
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid w-full max-w-sm items-center gap-1.5 mb-4">
              <FormLabel htmlFor="logo">{t("forms.company.logo")}</FormLabel>
              <Input 
                id="logo" 
                type="file" 
                accept="image/*"
                onChange={handleFileChange}
              />
              {selectedFile && (
                <p className="text-sm text-gray-600">
                  Archivo seleccionado: {selectedFile.name}
                </p>
              )}
              {companyData.logo && !selectedFile && (
                <p className="text-sm text-gray-600">
                  Logo actual: {companyData.logo}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="mt-4 bg-primary hover:bg-primary-600"
              disabled={loading}
            >
              {loading ? (
                <Loader style={"buttonLoader"} />
              ) : (
                t("buttonTexts.saveChanges")
              )}
            </Button>
          </form>
        </Form>
      </Card>
    </section>
  );
};
export default CreateCompanyForm;
